// 관리자 SPA 최상위 — 사이드바 + 8개 탭 + topbar (프로토타입 1:1 매핑)
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTab, PAGE_TITLES, type TabId } from './lib/tabStore';
import { useScanStore } from './lib/scanStore';
import { Overview } from './pages/Overview';
import { TestPage } from './pages/TestPage';
import { Logs } from './pages/Logs';
import { Quarantine } from './pages/Quarantine';
import { Policies } from './pages/Policies';
import { Engines } from './pages/Engines';
import { Settings } from './pages/Settings';
import { ApiPage } from './pages/ApiPage';
import { Activity } from './pages/Activity';
import { Manual } from './pages/Manual';
import { HashBlacklist } from './pages/HashBlacklist';
import { ToastHost } from './components/ToastHost';
import { ModalHost } from './components/ModalHost';
import { LoginGate } from './components/LoginGate';
import { useUI } from './lib/uiStore';
import { audit, clearSession, readSession } from './lib/auth';
import { adminGet, adminPost } from './lib/adminApi';
import { ThemeSwitcher } from './components/ThemeSwitcher';

interface NavItem { id: TabId; label: string; icon: string; count?: number; countKey?: 'logs' | 'quarantine' }
interface NavSection { label: string; items: NavItem[] }

const NAV: NavSection[] = [
  { label: '대시보드', items: [
    { id: 'overview', label: '개요', icon: '⊡' },
    { id: 'test', label: '파일검사', icon: '⬆' },
  ]},
  { label: '관리', items: [
    { id: 'logs', label: '검사 로그', icon: '📋', countKey: 'logs' },
    { id: 'quarantine', label: '격리소', icon: '🔒', countKey: 'quarantine' },
    { id: 'policies', label: '정책 관리', icon: '⚙' },
    { id: 'engines', label: '엔진 상태', icon: '⚡' },
    { id: 'settings', label: '시스템 설정', icon: '🛠' },
  ]},
  { label: '개발자', items: [
    { id: 'api', label: 'API 문서', icon: '⟨⟩' },
    { id: 'activity', label: '활동 로그', icon: '📡' },
    { id: 'blacklist', label: '해시 블랙리스트', icon: '🚫' },
  ]},
  { label: '도움말', items: [
    { id: 'manual', label: '사용설명서', icon: '📖' },
  ]},
];

const PANELS: Record<TabId, () => ReactNode> = {
  overview: () => <Overview />,
  test: () => <TestPage />,
  logs: () => <Logs />,
  quarantine: () => <Quarantine />,
  policies: () => <Policies />,
  engines: () => <Engines />,
  settings: () => <Settings />,
  api: () => <ApiPage />,
  activity: () => <Activity />,
  manual: () => <Manual />,
  blacklist: () => <HashBlacklist />,
};

interface HealthData {
  components: Record<string, { status: string; latencyMs?: number; detail?: string }>;
}
interface OverviewData {
  stats: { total: number; safe: number; quarantined: number; blocked: number; pending: number };
  queues: { scan: Record<string, number> };
}

const SEED_HEALTH: HealthData = {
  components: {
    clamav: { status: 'up', detail: 'ClamAV 0.103.11' },
    yara: { status: 'up', detail: 'YARA 4.5.1' },
    redis: { status: 'up', latencyMs: 1 },
    postgres: { status: 'up', latencyMs: 5 },
  },
};
const SEED_OVERVIEW: OverviewData = {
  stats: { total: 142, safe: 137, quarantined: 3, blocked: 2, pending: 0 },
  queues: { scan: {} },
};

export default function App() {
  const { tab, setTab } = useTab();
  const title = PAGE_TITLES[tab];
  const { showToast, bumpAuthVersion } = useUI();
  const session = readSession();
  const jobs = useScanStore((s) => s.jobs);
  const liveLogCount = useMemo(() => jobs.filter((j) => j.status !== 'pending' && j.status !== 'scanning').length, [jobs]);
  const liveQCount = useMemo(() => jobs.filter((j) => j.status === 'quarantine').length, [jobs]);

  const [health, setHealth] = useState<HealthData>(SEED_HEALTH);
  const [overview, setOverview] = useState<OverviewData>(SEED_OVERVIEW);

  useEffect(() => {
    let alive = true;
    const load = () => Promise.all([
      adminGet<HealthData>('/admin/health').catch(() => null),
      adminGet<OverviewData>('/admin/overview').catch(() => null),
    ]).then(([h, o]) => { if (alive) { if (h) setHealth(h); if (o) setOverview(o); } });
    load();
    const id = setInterval(load, 10_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const dbLogCount = overview.stats.total;
  const dbQCount = overview.stats.quarantined;
  const totalLogCount = liveLogCount + dbLogCount;
  const totalQCount = liveQCount + dbQCount;
  const scanQWaiting = Object.values(overview.queues.scan).reduce((a, b) => a + b, 0);

  const clamavStatus = health.components.clamav?.status === 'up';
  const yaraStatus = health.components.yara?.status === 'up';
  const redisStatus = health.components.redis?.status === 'up';
  const clamavDetail = health.components.clamav?.detail ?? 'ClamAV';
  const yaraDetail = health.components.yara?.detail ?? 'YARA';

  async function onDbUpdate() {
    showToast('ClamAV DB 업데이트 시작', 'info');
    try {
      const res = await adminPost<{ ok: boolean; output?: string; error?: string }>('/admin/db-update', {});
      if (res.ok) showToast('ClamAV DB 업데이트 완료', 'success');
      else showToast(`업데이트 실패: ${res.output ?? res.error ?? '알 수 없는 오류'}`, 'error');
    } catch {
      showToast('업데이트 요청 실패', 'error');
    }
  }

  function onLogout() {
    if (session) audit('logout', `user=${session.username}`);
    clearSession();
    bumpAuthVersion();
  }

  return (
    <LoginGate>
      <>
      <div className="app">
        <aside className="sidebar">
          <div className="brand">
            <div className="brand-mark">
              <div className="brand-icon">🛡</div>
              <div>
                <div className="brand-text">File<em>Guard</em></div>
                <div className="brand-tag">FILE SECURITY SERVICE</div>
              </div>
            </div>
          </div>

          <nav>
            {NAV.map((sec) => (
              <div className="nav-section" key={sec.label}>
                <div className="nav-label">{sec.label}</div>
                {sec.items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    className={`nav-item ${tab === it.id ? 'active' : ''}`}
                    onClick={() => setTab(it.id)}
                  >
                    <span className="ico">{it.icon}</span> {it.label}
                    {it.countKey === 'logs' && totalLogCount > 0 && (
                      <span className="count">{totalLogCount}</span>
                    )}
                    {it.countKey === 'quarantine' && totalQCount > 0 && (
                      <span className="count">{totalQCount}</span>
                    )}
                    {it.count !== undefined && <span className="count">{it.count}</span>}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div className="sidebar-foot">
            <div className="engine-row">
              <span className="dot" style={clamavStatus ? undefined : { background: 'var(--red)', boxShadow: 'none', animation: 'none' }} />
              {clamavDetail} {clamavStatus ? '(active)' : '(offline)'}
            </div>
            <div className="engine-row">
              <span className="dot" style={yaraStatus ? undefined : { background: 'var(--red)', boxShadow: 'none', animation: 'none' }} />
              {yaraDetail} {yaraStatus ? '(loaded)' : '(offline)'}
            </div>
            <div className="engine-row">
              <span className="dot" style={redisStatus ? undefined : { background: 'var(--red)', boxShadow: 'none', animation: 'none' }} />
              Redis Queue ({scanQWaiting} jobs)
            </div>
            <div style={{ marginTop: 10, color: 'var(--text3)' }}>v0.1.0</div>
          </div>
        </aside>

        <main className="main">
          <div className="topbar">
            <div className="page-title">
              {title.main} <em>{title.em}</em>
            </div>
            <div className="topbar-actions">
              {session && (
                <span className="text-mono text-muted" style={{ fontSize: 11, marginRight: 4 }}>
                  👤 {session.username}
                </span>
              )}
              <ThemeSwitcher />
              <button className="btn btn-ghost btn-sm" onClick={onDbUpdate}>🔄 DB 업데이트</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setTab('api')}>📖 문서</button>
              <button className="btn btn-pri btn-sm" onClick={() => setTab('test')}>검사 시작</button>
              <button className="btn btn-danger btn-sm" onClick={onLogout} aria-label="로그아웃">🚪 로그아웃</button>
            </div>
          </div>

          <div className="content">
            {(Object.keys(PANELS) as TabId[]).map((id) => (
              <div key={id} className={`tab-panel ${tab === id ? 'active' : ''}`} id={`panel-${id}`}>
                {tab === id && PANELS[id]()}
              </div>
            ))}
          </div>
        </main>
      </div>

      <ToastHost />
      <ModalHost />
      </>
    </LoginGate>
  );
}
